import type { ReactNode } from "react";
import { Box, Stack, useTheme } from "@mui/material";
import { FoodsTags, Typography, type FoodsTagsProps } from "@foodpilot/foods";

export const productInformationNoValue = "-";

type InfoFieldProps = {
    label: string;
    lineHeight?: number | string;
    value?: ReactNode;
    tone?: "primary" | "secondary";
};

export const InfoField = (props: InfoFieldProps) => {
    const { label, lineHeight, value = productInformationNoValue, tone = "primary" } = props;
    const theme = useTheme();

    return (
        <Stack gap={theme.spacing(0.5)} alignSelf="stretch" minWidth={0}>
            <Typography variant="body-semibold" sx={{ lineHeight: lineHeight ?? theme.spacing(2.75) }}>
                {label}
            </Typography>
            {typeof value === "string" || typeof value === "number" ?
                <Typography
                    variant="body-regular"
                    sx={{
                        color: theme.custom.grey[tone === "primary" ? 2800 : 2500],
                        lineHeight: lineHeight ?? theme.spacing(2.75),
                    }}
                >
                    {value === "" || value === null || value === undefined ? productInformationNoValue : value}
                </Typography>
            :   value}
        </Stack>
    );
};

type InfoSectionProps = {
    sectionKey: string;
    title: string;
    children: ReactNode;
    gap?: number | string;
    titleLineHeight?: number | string;
    titleVariant?: "big-medium" | "body-medium";
};

export const InfoSection = (props: InfoSectionProps) => {
    const { sectionKey, title, children, gap, titleLineHeight, titleVariant = "big-medium" } = props;
    const theme = useTheme();

    return (
        <Stack data-information-section={sectionKey} gap={gap ?? theme.spacing(2)} alignSelf="stretch">
            <Typography
                variant={titleVariant}
                sx={{
                    color: theme.custom.grey[2000],
                    lineHeight: titleLineHeight ?? theme.spacing(titleVariant === "big-medium" ? 3.25 : 2.75),
                    textTransform: "uppercase",
                }}
            >
                {title}
            </Typography>
            {children}
        </Stack>
    );
};

export type DisplayTag = {
    key: string;
    label: string;
    detail?: string;
    colors?: FoodsTagsProps["colors"];
    endIcon?: FoodsTagsProps["endIcon"];
    onClick?: FoodsTagsProps["onClick"];
};

type DisplayTagListProps = {
    items: DisplayTag[];
    spacing?: "compact" | "lifecycle";
};

export const DisplayTagList = (props: DisplayTagListProps) => {
    const { items, spacing = "compact" } = props;
    const theme = useTheme();
    const gap = spacing === "lifecycle" ? theme.spacing(2) : theme.spacing(1);

    if (items.length === 0) {
        return <Typography variant="body-regular">{productInformationNoValue}</Typography>;
    }

    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap }}>
            {items.map((item) => (
                <FoodsTags
                    key={item.key}
                    label={item.label}
                    detail={item.detail}
                    endIcon={item.endIcon ?? (item.onClick ? "openNewTab" : undefined)}
                    onClick={item.onClick}
                    size="display"
                    sx={spacing === "compact" ? { height: theme.spacing(3.5) } : { borderWidth: theme.spacing(0) }}
                    colors={item.colors}
                />
            ))}
        </Box>
    );
};

export const CategoryTagList = ({
    labels,
    spacing,
}: {
    labels: string[];
    spacing?: DisplayTagListProps["spacing"];
}) => <DisplayTagList items={labels.map((label) => ({ key: label, label }))} spacing={spacing} />;

export const InformationDivider = () => {
    const theme = useTheme();
    return (
        <Box role="separator" aria-orientation="horizontal" sx={{ height: 0, position: "relative", width: "100%" }}>
            <Box
                aria-hidden
                sx={{
                    borderBottomColor: theme.custom.strokes.containerDivider.color,
                    borderBottomStyle: theme.custom.strokes.containerDivider.style,
                    borderBottomWidth: theme.custom.strokes.containerDivider.width,
                    left: 0,
                    position: "absolute",
                    right: 0,
                    top: 0,
                }}
            />
        </Box>
    );
};
